/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { 
  // Patient, 
  Appointment, Call, ConversationMessage, 
  OperationalMetrics, AIAgentMetrics, Voicemail, Task, 
  OmnichannelMessage, EngagementFunnel, ClinicalBusinessCorrelation, 
  PredictiveForecasting } from '../types';
import { dummyPatients } from '../data/dummyPatients';
import { dummyAppointments } from '../data/dummyAppointments';
import { dummyCalls } from '../data/dummyCalls';
import { dummyReminders } from '../data/dummyReminders';
import { UserPatient } from '../services/api/user/userApi';

interface AppState {
  patients: UserPatient[];
  appointments: Appointment[];
  calls: Call[];
  reminders: any[];
  voicemails: Voicemail[];
  tasks: Task[];
  omnichannelMessages: OmnichannelMessage[];
  incomingCalls: Call[];
  activeCall: Call | null;
  selectedPatient: UserPatient | null;
  language: 'en' | 'es';
  currentView: 'dashboard' | 'calls' | 'patients' | 'appointments' | 'analytics' | 'reminders' | 'education' | 'settings';
  operationalMetrics: OperationalMetrics;
  aiAgentMetrics: AIAgentMetrics;
  engagementFunnel: EngagementFunnel;
  clinicalCorrelation: ClinicalBusinessCorrelation;
  predictiveForecasting: PredictiveForecasting;
}

type AppAction =
  | { type: 'SET_PATIENTS'; payload: UserPatient[] }
  | { type: 'ADD_PATIENT'; payload: UserPatient }
  | { type: 'UPDATE_PATIENT'; payload: UserPatient }
  | { type: 'DELETE_PATIENT'; payload: string }
  | { type: 'SET_APPOINTMENTS'; payload: Appointment[] }
  | { type: 'ADD_APPOINTMENT'; payload: Appointment }
  | { type: 'UPDATE_APPOINTMENT'; payload: Appointment }
  | { type: 'DELETE_APPOINTMENT'; payload: string }
  | { type: 'ADD_CALL'; payload: Call }
  | { type: 'UPDATE_CALL'; payload: Call }
  | { type: 'DELETE_CALL'; payload: string }
  | { type: 'ADD_VOICEMAIL'; payload: Voicemail }
  | { type: 'UPDATE_VOICEMAIL'; payload: Voicemail }
  | { type: 'ADD_TASK'; payload: Task }
  | { type: 'UPDATE_TASK'; payload: Task }
  | { type: 'ADD_OMNICHANNEL_MESSAGE'; payload: OmnichannelMessage }
  | { type: 'ADD_INCOMING_CALL'; payload: Call }
  | { type: 'REMOVE_INCOMING_CALL'; payload: string }
  | { type: 'ACCEPT_INCOMING_CALL'; payload: string }
  | { type: 'SET_ACTIVE_CALL'; payload: Call | null }
  | { type: 'SET_SELECTED_PATIENT'; payload: UserPatient | null }
  | { type: 'SET_LANGUAGE'; payload: 'en' | 'es' }
  | { type: 'SET_CURRENT_VIEW'; payload: AppState['currentView'] }
  | { type: 'ADD_MESSAGE'; payload: { callId: string; message: ConversationMessage } };

const generateIncomingCalls = (): Call[] => {
  const callTypes = [
    { purpose: 'emergency' as const, urgency: 'emergency' as const, message: 'I have severe tooth pain and need help immediately!' },
    { purpose: 'appointment' as const, urgency: 'medium' as const, message: 'I need to schedule a cleaning appointment.' },
    { purpose: 'inquiry' as const, urgency: 'low' as const, message: 'I have questions about my insurance coverage.' },
    { purpose: 'reminder' as const, urgency: 'low' as const, message: 'I received a reminder call but missed it.' },
    { purpose: 'follow-up' as const, urgency: 'medium' as const, message: 'I need to follow up on my recent procedure.' }
  ];

  return callTypes.map((callType, index) => {
    const patient: UserPatient | undefined = dummyPatients[index % 5] as UserPatient;
    return {
      id: `incoming-${index + 1}`,
      patientId: patient ? String(patient.id) : `unknown-${index + 1}`,
      patientName: patient ? patient.first_name : 'Unknown Patient',
      startTime: new Date(Date.now() - (index * 30000)), // Stagger call times
      status: 'dialing',
      type: 'inbound',
      purpose: callType.purpose,
      urgencyLevel: callType.urgency,
      messages: [],
    };
  });
};

const generateOperationalMetrics = (): OperationalMetrics => ({
  operationalHealthGauge: 87,
  noShowRate: {
    current: 8.5,
    target: 5.0,
    trend: 'down'
  },
  chairUtilizationData: [
    {
      chairId: 'chair-1',
      chairName: 'Chair 1 - Dr. Estrabillo',
      provider: 'Dr. Estrabillo',
      utilizationRate: 92,
      hoursBooked: 7.4,
      totalHours: 8,
      efficiency: 'high'
    },
    {
      chairId: 'chair-2',
      chairName: 'Chair 2 - Dr. Martinez',
      provider: 'Dr. Martinez',
      utilizationRate: 78,
      hoursBooked: 6.2,
      totalHours: 8,
      efficiency: 'medium'
    },
    {
      chairId: 'chair-3',
      chairName: 'Chair 3 - Hygienist',
      provider: 'Sarah (Hygienist)',
      utilizationRate: 85,
      hoursBooked: 6.8,
      totalHours: 8,
      efficiency: 'high'
    }
  ],
  patientRetentionRate: {
    current: 94.2,
    target: 90.0,
    trend: 'up'
  },
  uptimeMaximizerInsights: [
    {
      type: 'chronic_canceller',
      patientId: '2',
      patientName: 'James Patterson',
      frequency: 3,
      lastOccurrence: '2025-01-10',
      recommendation: 'Consider requiring confirmation 24 hours before appointment'
    },
    {
      type: 'frequent_reschedule',
      patientId: '5',
      patientName: 'Jennifer Walsh',
      frequency: 4,
      lastOccurrence: '2025-01-08',
      recommendation: 'Offer flexible scheduling options or morning appointments'
    }
  ]
});

const generateAIAgentMetrics = (): AIAgentMetrics => ({
  appointmentsBookedByAI: {
    today: 12,
    thisWeek: 47,
    thisMonth: 186,
    trend: 'up'
  },
  callResolutionRate: {
    rate: 87.3,
    target: 85.0,
    trend: 'up'
  },
  patientSatisfactionScore: {
    score: 4.6,
    target: 4.5,
    trend: 'stable',
    recentFeedback: [
      {
        patientId: '1',
        patientName: 'Maria Elena Rodriguez',
        rating: 5,
        comment: 'El asistente de IA fue muy útil y programó mi cita de emergencia rápidamente.',
        date: '2025-01-15',
        callId: '1'
      },
      {
        patientId: '2',
        patientName: 'James Patterson',
        rating: 4,
        comment: 'Quick and efficient. Got my appointment scheduled without any issues.',
        date: '2025-01-14',
        callId: '2'
      }
    ]
  },
  seamlessHandoffStatus: {
    queueLength: 2,
    avgWaitTime: 45,
    escalationRate: 12.7,
    activeHandoffs: [
      {
        callId: 'handoff-1',
        patientName: 'Roberto Gonzalez',
        reason: 'Complex insurance verification needed',
        waitTime: 120,
        priority: 'medium',
        assignedAgent: 'Charlene'
      },
      {
        callId: 'handoff-2',
        patientName: 'Emergency Patient',
        reason: 'Severe dental emergency',
        waitTime: 30,
        priority: 'high'
      }
    ]
  }
});

// Generate dummy voicemails
const generateVoicemails = (): Voicemail[] => [
  {
    id: 'vm-1',
    patientId: '1',
    patientName: 'Maria Elena Rodriguez',
    phone: '+1 (555) 123-4567',
    timestamp: new Date(Date.now() - 3600000),
    duration: 45,
    transcription: 'Hola, soy María Rodriguez. Tengo mucho dolor en mi muela y necesito una cita de emergencia lo antes posible. Mi número es 555-123-4567. Gracias.',
    aiAnalysis: {
      intent: 'emergency_appointment',
      confidence: 0.95,
      urgencyLevel: 'emergency',
      suggestedAction: 'Schedule emergency appointment within 24 hours',
      autoBookingAttempted: true,
      appointmentBooked: true
    },
    status: 'completed'
  },
  {
    id: 'vm-2',
    patientId: '2',
    patientName: 'James Patterson',
    phone: '+1 (555) 234-5678',
    timestamp: new Date(Date.now() - 7200000),
    duration: 32,
    transcription: 'Hi, this is James Patterson. I wanted to check if my crown is ready for pickup. I think it was supposed to be done this week. Please call me back at 555-234-5678.',
    aiAnalysis: {
      intent: 'crown_status_inquiry',
      confidence: 0.88,
      urgencyLevel: 'medium',
      suggestedAction: 'Check lab status and call patient back with update',
      autoBookingAttempted: false
    },
    status: 'new'
  },
  {
    id: 'vm-3',
    patientId: '5',
    patientName: 'Jennifer Walsh',
    phone: '+1 (555) 567-8901',
    timestamp: new Date(Date.now() - 10800000),
    duration: 28,
    transcription: 'Hi, it\'s Jennifer Walsh. I need to reschedule my appointment next week because of a work conflict. Can someone please call me back to find a new time? Thanks.',
    aiAnalysis: {
      intent: 'reschedule_appointment',
      confidence: 0.92,
      urgencyLevel: 'medium',
      suggestedAction: 'Offer alternative appointment times within same week',
      autoBookingAttempted: false
    },
    status: 'reviewed'
  }
];

// Generate dummy tasks
const generateTasks = (): Task[] => [
  {
    id: 'task-1',
    patientId: '2',
    patientName: 'James Patterson',
    type: 'callback',
    priority: 'medium',
    title: 'Crown Status Inquiry Follow-up',
    description: 'Patient called asking about crown status. Lab confirmed completion.',
    suggestedAction: 'Call patient to confirm crown is ready for pickup appointment',
    createdBy: 'ai',
    createdAt: new Date(Date.now() - 7200000),
    dueDate: new Date(Date.now() + 3600000),
    status: 'pending',
    relatedCommunication: {
      type: 'voicemail',
      id: 'vm-2'
    }
  },
  {
    id: 'task-2',
    patientId: '4',
    patientName: 'Roberto Gonzalez',
    type: 'insurance-verification',
    priority: 'high',
    title: 'Verify Insurance Coverage for Cardiac Patient',
    description: 'Patient has heart condition requiring special protocols. Verify coverage for extended appointment time.',
    suggestedAction: 'Contact Guardian insurance to confirm coverage for cardiac risk management protocols',
    createdBy: 'ai',
    createdAt: new Date(Date.now() - 14400000),
    dueDate: new Date(Date.now() + 86400000),
    status: 'in-progress',
    assignedTo: 'Sarah (Insurance Coordinator)'
  },
  {
    id: 'task-3',
    patientId: '5',
    patientName: 'Jennifer Walsh',
    type: 'follow-up',
    priority: 'low',
    title: 'Sedation Appointment Preparation',
    description: 'Patient scheduled for sedation cleaning. Ensure pre-appointment instructions sent.',
    suggestedAction: 'Send sedation preparation instructions 48 hours before appointment',
    createdBy: 'ai',
    createdAt: new Date(Date.now() - 86400000),
    dueDate: new Date(Date.now() + 172800000),
    status: 'completed'
  }
];

// Generate dummy omnichannel messages
const generateOmnichannelMessages = (): OmnichannelMessage[] => [
  {
    id: 'omni-1',
    patientId: '1',
    patientName: 'Maria Elena Rodriguez',
    channel: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 1800000),
    content: '¿Está lista mi corona? Tengo cita mañana.',
    status: 'read',
    aiContext: {
      clinicalDataUsed: ['Treatment Plan: Crown Placement - Tooth #14', 'Lab Status: Completed'],
      contextAwareResponse: true,
      confidence: 0.94
    }
  },
  {
    id: 'omni-2',
    patientId: '1',
    patientName: 'Maria Elena Rodriguez',
    channel: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1740000),
    content: 'Hola María! Sí, su corona está lista. Su cita de mañana a las 10:00 AM está confirmada para la colocación. Por favor llegue 15 minutos antes.',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Lab Status: Crown Ready', 'Appointment: Tomorrow 10:00 AM'],
      contextAwareResponse: true,
      confidence: 0.96
    }
  },
  {
    id: 'omni-2b',
    patientId: '1',
    patientName: 'Maria Elena Rodriguez',
    channel: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 1680000),
    content: 'Perfecto, gracias. ¿Necesito tomar algún medicamento antes?',
    status: 'read'
  },
  {
    id: 'omni-2c',
    patientId: '1',
    patientName: 'Maria Elena Rodriguez',
    channel: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1620000),
    content: 'No es necesario tomar medicamentos antes. Solo evite comer 2 horas antes de la cita. Veo en su historial que tiene diabetes, así que coordinaremos el horario de sus comidas.',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Medical History: Diabetes Type 2', 'Pre-procedure Instructions'],
      contextAwareResponse: true,
      confidence: 0.92
    }
  },
  {
    id: 'omni-3',
    patientId: '3',
    patientName: 'Dr. Ashley Chen',
    channel: 'email',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 3600000),
    content: 'I wanted to confirm that all latex-free protocols will be in place for my root canal procedure next week.',
    subject: 'Latex-Free Protocol Confirmation',
    status: 'read',
    aiContext: {
      clinicalDataUsed: ['Allergy: Latex', 'Procedure: Root Canal Therapy - Tooth #14'],
      contextAwareResponse: true,
      confidence: 0.98
    }
  },
  {
    id: 'omni-4',
    patientId: '3',
    patientName: 'Dr. Ashley Chen',
    channel: 'email',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 3540000),
    content: 'Dear Dr. Chen, Absolutely! Your latex allergy is prominently flagged in our system. All materials, gloves, and equipment for your root canal procedure will be completely latex-free. We have also attached a detailed list of all materials that will be used. Your appointment is confirmed for January 20th at 11:00 AM.',
    subject: 'Re: Latex-Free Protocol Confirmation',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Allergy Alert: Latex', 'Latex-Free Material List', 'Appointment Details'],
      contextAwareResponse: true,
      confidence: 0.97
    }
  },
  {
    id: 'omni-5a',
    patientId: '2',
    patientName: 'James Patterson',
    channel: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 5400000),
    content: 'Hi, I have severe tooth pain on my upper right side. It started this morning and it\'s getting worse. Can I get an emergency appointment?',
    status: 'read'
  },
  {
    id: 'omni-5b',
    patientId: '2',
    patientName: 'James Patterson',
    channel: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 5340000),
    content: 'Hi James, I understand you\'re experiencing severe tooth pain. On a scale of 1-10, how would you rate your pain? I see you have a history of teeth grinding - this could be related.',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Medical History: Bruxism', 'Previous Night Guard Treatment'],
      contextAwareResponse: true,
      confidence: 0.89
    }
  },
  {
    id: 'omni-5c',
    patientId: '2',
    patientName: 'James Patterson',
    channel: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 5280000),
    content: 'It\'s about an 8/10. Very sharp pain when I bite down. I think my night guard might be cracked.',
    status: 'read'
  },
  {
    id: 'omni-5d',
    patientId: '2',
    patientName: 'James Patterson',
    channel: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 5220000),
    content: 'That level of pain requires immediate attention. I\'ve scheduled an emergency appointment for you today at 3:30 PM. Please stop using your night guard immediately if it\'s damaged. Dr. Estrabillo will examine both the pain and your night guard.',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Emergency Scheduling Protocol', 'Available Appointments', 'Night Guard History'],
      contextAwareResponse: true,
      confidence: 0.95
    }
  },
  {
    id: 'omni-6a',
    patientId: '4',
    patientName: 'Roberto Gonzalez',
    channel: 'email',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 7200000),
    content: 'Hola, necesito programar mi limpieza de rutina. Mi cardiólogo dice que necesito tomar antibióticos antes. ¿Pueden ayudarme con esto?',
    subject: 'Cita de Limpieza - Medicación Cardíaca',
    status: 'read'
  },
  {
    id: 'omni-6b',
    patientId: '4',
    patientName: 'Roberto Gonzalez',
    channel: 'email',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 7140000),
    content: 'Hola Sr. Gonzalez, por supuesto podemos ayudarle. Veo en su historial que tiene enfermedad cardíaca y toma anticoagulantes. Coordinaremos con su cardiólogo Dr. Martinez para el protocolo de antibióticos. He programado su limpieza para el 25 de enero a las 9:00 AM. Le enviaremos las instrucciones de medicación 3 días antes.',
    subject: 'Re: Cita de Limpieza - Medicación Cardíaca',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Medical History: Heart Disease', 'Medications: Blood Thinners', 'Cardiologist: Dr. Martinez'],
      contextAwareResponse: true,
      confidence: 0.93
    }
  },
  {
    id: 'omni-7a',
    patientId: '5',
    patientName: 'Jennifer Walsh',
    channel: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 9000000),
    content: 'I\'m really nervous about my upcoming cleaning. Can I get sedation? I have dental anxiety.',
    status: 'read'
  },
  {
    id: 'omni-7b',
    patientId: '5',
    patientName: 'Jennifer Walsh',
    channel: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 8940000),
    content: 'Hi Jennifer, I completely understand your anxiety. I see you\'ve had positive experiences with nitrous oxide before. We can definitely arrange sedation for your cleaning. I\'ll also note that you\'re allergic to ibuprofen, so we\'ll use alternative pain management if needed.',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Medical History: Anxiety', 'Previous Sedation: Nitrous Oxide', 'Allergy: Ibuprofen'],
      contextAwareResponse: true,
      confidence: 0.91
    }
  },
  {
    id: 'omni-7c',
    patientId: '5',
    patientName: 'Jennifer Walsh',
    channel: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 8880000),
    content: 'That would be perfect! When can we schedule it? And will I be able to drive home after?',
    status: 'read'
  },
  {
    id: 'omni-7d',
    patientId: '5',
    patientName: 'Jennifer Walsh',
    channel: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 8820000),
    content: 'With nitrous oxide, you\'ll be able to drive home safely. I\'ve scheduled your sedation cleaning for February 5th at 1:00 PM. You\'ll receive pre-appointment instructions, but no special preparation is needed for nitrous oxide.',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Sedation Protocols', 'Available Appointments', 'Nitrous Oxide Guidelines'],
      contextAwareResponse: true,
      confidence: 0.94
    }
  },
  {
    id: 'omni-8a',
    patientId: '6',
    patientName: 'Michael Thompson',
    channel: 'email',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 10800000),
    content: 'I need to know what my insurance covers for a root canal. My tooth has been bothering me and I think I need treatment.',
    subject: 'Insurance Coverage Question - Root Canal',
    status: 'read'
  },
  {
    id: 'omni-8b',
    patientId: '6',
    patientName: 'Michael Thompson',
    channel: 'email',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 10740000),
    content: 'Hi Michael, I\'ve checked your Delta Dental coverage. Root canal therapy is covered at 80% after your deductible. For a typical root canal, your out-of-pocket cost would be approximately $240-$320. I\'d recommend scheduling a consultation first to confirm the diagnosis and treatment plan. Would you like me to schedule that for you?',
    subject: 'Re: Insurance Coverage Question - Root Canal',
    status: 'delivered',
    aiContext: {
      clinicalDataUsed: ['Insurance: Delta Dental', 'Coverage: 80% Endodontics', 'Deductible Status'],
      contextAwareResponse: true,
      confidence: 0.87
    }
  },
  {
    id: 'omni-5',
    patientId: '5',
    patientName: 'Jennifer Walsh',
    channel: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 12600000),
    content: 'Hi Jennifer! This is a reminder about your sedation cleaning appointment on Feb 5th at 1:00 PM. Please remember not to eat 2 hours before your appointment.',
    status: 'delivered',
    automatedWorkflow: {
      triggered: true,
      workflowType: 'recall',
      nextAction: 'If no response in 24 hours, escalate to AI phone call',
      scheduledAt: new Date(Date.now() + 86400000)
    }
  }
];

// Generate engagement funnel data
const generateEngagementFunnelData = (): EngagementFunnel => ({
  totalContacts: 1250,
  steps: [
    {
      step: 'Recare Emails Sent',
      count: 1250,
      conversionRate: 100,
      dropOffRate: 0
    },
    {
      step: 'Email Opened',
      count: 875,
      conversionRate: 70,
      dropOffRate: 30
    },
    {
      step: 'Clicked to Book',
      count: 262,
      conversionRate: 30,
      dropOffRate: 70
    },
    {
      step: 'Appointment Scheduled',
      count: 210,
      conversionRate: 80,
      dropOffRate: 20
    },
    {
      step: 'Appointment Completed',
      count: 189,
      conversionRate: 90,
      dropOffRate: 10
    }
  ],
  bottleneckInsight: {
    step: 'Clicked to Book',
    issue: 'Only 30% of patients who open emails actually click to book appointments',
    recommendation: 'Simplify the booking process and add more prominent call-to-action buttons',
    impact: 'Improving this step by 15% could result in 131 additional appointments per campaign'
  }
});

// Generate clinical correlation data
const generateClinicalCorrelationData = (): ClinicalBusinessCorrelation => ({
  correlations: [
    {
      treatmentType: 'Crown Procedures',
      baseAcceptanceRate: 65,
      withEducationRate: 88,
      improvement: 35,
      sampleSize: 156
    },
    {
      treatmentType: 'Root Canal Therapy',
      baseAcceptanceRate: 72,
      withEducationRate: 89,
      improvement: 24,
      sampleSize: 89
    },
    {
      treatmentType: 'Periodontal Treatment',
      baseAcceptanceRate: 58,
      withEducationRate: 79,
      improvement: 36,
      sampleSize: 134
    },
    {
      treatmentType: 'Cosmetic Procedures',
      baseAcceptanceRate: 45,
      withEducationRate: 68,
      improvement: 51,
      sampleSize: 78
    }
  ],
  keyInsight: {
    finding: 'Patients who watched educational videos within 24 hours of consultation had 35% higher case acceptance rates',
    impact: 'Educational content timing is critical - immediate post-consultation delivery shows highest engagement',
    recommendation: 'Implement automated educational content delivery within 2 hours of consultation completion',
    confidence: 92
  }
});

// Generate predictive forecasting data
const generatePredictiveForecastingData = (): PredictiveForecasting => ({
  forecasts: [
    {
      month: 'February 2025',
      predictedProduction: 125000,
      currentBookings: 85000,
      treatmentPipeline: 45000,
      seasonalAdjustment: -5000,
      confidence: 87
    },
    {
      month: 'March 2025',
      predictedProduction: 142000,
      currentBookings: 95000,
      treatmentPipeline: 52000,
      seasonalAdjustment: -5000,
      confidence: 82
    },
    {
      month: 'April 2025',
      predictedProduction: 158000,
      currentBookings: 105000,
      treatmentPipeline: 58000,
      seasonalAdjustment: -5000,
      confidence: 78
    },
    {
      month: 'May 2025',
      predictedProduction: 165000,
      currentBookings: 110000,
      treatmentPipeline: 62000,
      seasonalAdjustment: -7000,
      confidence: 75
    }
  ],
  keyInsight: {
    trend: 'increasing',
    prediction: 'Practice production expected to grow 32% over next 4 months based on current treatment pipeline',
    recommendation: 'Consider hiring additional hygienist by March to handle increased patient volume',
    riskFactors: [
      'Seasonal decline in May typically reduces bookings by 15%',
      'Current treatment pipeline heavily weighted toward high-value procedures',
      'Staff capacity may become bottleneck if growth continues'
    ]
  },
  resourceNeeds: {
    staffing: 'Additional hygienist needed by March 2025',
    equipment: 'Consider second operatory chair by April 2025',
    scheduling: 'Extend evening hours to accommodate 25% increase in appointments'
  }
});

// Get initial view from localStorage, default to dashboard
const getInitialView = (): AppState['currentView'] => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return 'dashboard';
  }
  
  try {
    const savedView = localStorage.getItem('currentView');
    if (savedView) {
      // Validate that it's a valid view
      const validViews = ['dashboard', 'calls', 'patients', 'appointments', 'analytics', 'reminders', 'education', 'settings'];
      if (validViews.includes(savedView)) {
        return savedView as AppState['currentView'];
      }
    }
  } catch (error) {
    console.error('Error reading currentView from localStorage:', error);
  }
  return 'dashboard';
};

const initialState: AppState = {
  patients: dummyPatients as UserPatient[],
  appointments: dummyAppointments,
  calls: dummyCalls,
  reminders: dummyReminders,
  voicemails: generateVoicemails(),
  tasks: generateTasks(),
  omnichannelMessages: generateOmnichannelMessages(),
  incomingCalls: generateIncomingCalls(),
  activeCall: null,
  selectedPatient: null,
  language: 'en',
  currentView: getInitialView(),
  operationalMetrics: generateOperationalMetrics(),
  aiAgentMetrics: generateAIAgentMetrics(),
  engagementFunnel: generateEngagementFunnelData(),
  clinicalCorrelation: generateClinicalCorrelationData(),
  predictiveForecasting: generatePredictiveForecastingData()
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_PATIENTS':
      return { ...state, patients: action.payload };
    case 'ADD_PATIENT':
      return { ...state, patients: [...state.patients, action.payload] };
    case 'UPDATE_PATIENT':
      return {
        ...state,
        patients: state.patients.map(patient => 
          patient.id === action.payload.id ? action.payload : patient
        ),
        selectedPatient: state.selectedPatient?.id === action.payload.id ? action.payload : state.selectedPatient
      };
    case 'DELETE_PATIENT':
      return {
        ...state,
        patients: state.patients.filter(patient => +patient.id !== +action.payload),
        selectedPatient: state.selectedPatient && +state.selectedPatient?.id === +action.payload ? null : state.selectedPatient
      };
    case 'SET_APPOINTMENTS':
      return { ...state, appointments: action.payload };
    case 'ADD_APPOINTMENT':
      return { ...state, appointments: [...state.appointments, action.payload] };
    case 'UPDATE_APPOINTMENT':
      return {
        ...state,
        appointments: state.appointments.map(apt => 
          apt.id === action.payload.id ? action.payload : apt
        )
      };
    case 'DELETE_APPOINTMENT':
      return {
        ...state,
        appointments: state.appointments.filter(apt => apt.id !== action.payload)
      };
    case 'ADD_CALL':
      return { ...state, calls: [...state.calls, action.payload] };
    case 'UPDATE_CALL':
      return {
        ...state,
        calls: state.calls.map(call => call.id === action.payload.id ? action.payload : call),
        activeCall: state.activeCall?.id === action.payload.id ? action.payload : state.activeCall
      };
    case 'DELETE_CALL':
      return {
        ...state,
        calls: state.calls.filter(call => call.id !== action.payload),
        activeCall: state.activeCall?.id === action.payload ? null : state.activeCall
      };
    case 'ADD_VOICEMAIL':
      return { ...state, voicemails: [...state.voicemails, action.payload] };
    case 'UPDATE_VOICEMAIL':
      return {
        ...state,
        voicemails: state.voicemails.map(vm => vm.id === action.payload.id ? action.payload : vm)
      };
    case 'ADD_TASK':
      return { ...state, tasks: [...state.tasks, action.payload] };
    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: state.tasks.map(task => task.id === action.payload.id ? action.payload : task)
      };
    case 'ADD_OMNICHANNEL_MESSAGE':
      return { ...state, omnichannelMessages: [...state.omnichannelMessages, action.payload] };
    case 'ADD_INCOMING_CALL':
      return { ...state, incomingCalls: [...state.incomingCalls, action.payload] };
    case 'REMOVE_INCOMING_CALL':
      return {
        ...state,
        incomingCalls: state.incomingCalls.filter(call => call.id !== action.payload)
      };
    // case 'ACCEPT_INCOMING_CALL':
    //   { const incomingCall = state.incomingCalls.find(call => call.id === action.payload);
    //   if (incomingCall) {
    //     const patient = state.patients.find(p => +p.id === +incomingCall.patientId);
    //     const acceptedCall: Call = {
    //       ...incomingCall,
    //       status: 'connected',
    //       messages: [{
    //         id: Date.now().toString(),
    //         speaker: 'ai',
    //         content: patient?.language === 'es' 
    //           ? '¡Hola! Gracias por llamar a Estrabillo Dental Group. Soy su Asistente de Pacientes. ¿Cómo puedo ayudarle hoy?'
    //           : 'Hello! Thank you for calling Estrabillo Dental Group. This is your Patient Support Wizard. How can I help you today?',
    //         timestamp: new Date(),
    //         language: patient?.language || 'en'
    //       }]
    //     };
        
    //     return {
    //       ...state,
    //       incomingCalls: state.incomingCalls.filter(call => call.id !== action.payload),
    //       calls: [...state.calls, acceptedCall],
    //       activeCall: acceptedCall,
    //       selectedPatient: patient || null,
    //       language: patient?.language || 'en'
    //     };
    //   }
    //   return state; }
    case 'SET_ACTIVE_CALL':
      return { ...state, activeCall: action.payload };
    case 'SET_SELECTED_PATIENT':
      return { ...state, selectedPatient: action.payload };
    case 'SET_LANGUAGE':
      return { ...state, language: action.payload };
    case 'SET_CURRENT_VIEW':
      // Save to localStorage for persistence across refreshes
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem('currentView', action.payload);
        } catch (error) {
          console.error('Error saving currentView to localStorage:', error);
        }
      }
      return { ...state, currentView: action.payload };
    case 'ADD_MESSAGE':
      return {
        ...state,
        calls: state.calls.map(call => 
          call.id === action.payload.callId 
            ? { ...call, messages: [...call.messages, action.payload.message] }
            : call
        ),
        activeCall: state.activeCall?.id === action.payload.callId 
          ? { ...state.activeCall, messages: [...state.activeCall.messages, action.payload.message] }
          : state.activeCall
      };
    default:
      return state;
  }
}

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
}